/**
 * Dentulu Pro Callback Page
 * ===========================
 * Handles authentication callback from Dentulu Pro SSO popup.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/api/client';

export default function DentuluCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processDentuluCallback = async () => {
      try {
        // Get tokens from URL params (backend redirects with them after SSO)
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('message');

        // Check for error in callback
        if (errorParam) {
          const message = errorDescription || errorParam.replace(/_/g, ' ');
          setError(message);
          setIsProcessing(false);
          if (window.opener) {
            window.opener.postMessage({
              type: 'dentulu-auth-error',
              message: message,
            }, window.location.origin);
          }
          return;
        }

        // Validate required parameters
        if (!accessToken || !refreshToken) {
          setError('Missing authentication tokens from Dentulu Pro');
          setIsProcessing(false);
          if (window.opener) {
            window.opener.postMessage({
              type: 'dentulu-auth-error',
              message: 'Missing authentication tokens',
            }, window.location.origin);
          }
          return;
        }

        // Set auth header and fetch user profile
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        let user;
        try {
          const userResponse = await apiClient.get('/auth/me');
          user = userResponse.data;
        } catch (err) {
          console.error('Failed to fetch user profile:', err);
          setError('Failed to fetch user profile after Dentulu login.');
          setIsProcessing(false);
          return;
        }

        // Set authentication in our app store
        setAuth(accessToken, refreshToken, user);

        // Notify parent window about success (if popup)
        if (window.opener) {
          window.opener.postMessage({ type: 'dentulu-auth-success' }, window.location.origin);
          setTimeout(() => window.close(), 500);
        } else {
          // If not in popup, navigate to dashboard
          toast.success(`Welcome, ${user.first_name || user.email || 'User'}!`);
          window.location.replace('/dashboard');
        }
      } catch (err: any) {
        console.error('Dentulu Pro authentication error:', err);
        const errorMessage = 'Failed to process Dentulu Pro authentication. Please try again.';
        setError(errorMessage);
        setIsProcessing(false);
        if (window.opener) {
          window.opener.postMessage({
            type: 'dentulu-auth-error',
            message: errorMessage,
          }, window.location.origin);
        }
      }
    };

    processDentuluCallback();
  }, [searchParams, setAuth, navigate]);

  // Processing state
  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authenticating with Dentulu Pro
          </h2>
          <p className="text-gray-600">
            Please wait while we complete your authentication...
          </p>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Failed
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            {window.opener ? (
              <Button onClick={() => window.close()} className="w-full">
                Close Window
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} className="w-full">
                Back to Login
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return null;
}
